import { useEffect, useState } from "react";
import {
  channelFlowStart,
  ChannelStates,
} from "@/components/badge/channel-state";

type CFlow = Record<string, string[]>;

// Nexts graph of the channel flow, mermaid-rendered like the version flow
// graph and 1:1 with the configuration: nodes are the enabled states (the
// flow's keys — unreachable ones are pruned by the editor), arrows exactly
// the configured nexts — the editor's draft previews live.
const toMermaid = (flow: CFlow) => {
  const lines = ["flowchart LR"];
  // Virtual Start node → the entry state a newly added channel lands in.
  lines.push(`  start((Start)) --> ${channelFlowStart(flow)}`);
  for (const s of Object.keys(ChannelStates)) {
    if (!(s in flow)) continue;
    lines.push(`  ${s}["${ChannelStates[s]?.label ?? s}"]`);
  }
  for (const [from, nexts] of Object.entries(flow)) {
    if (from === "start") continue;
    for (const to of nexts) lines.push(`  ${from} --> ${to}`);
  }
  return lines.join("\n");
};

let renderSeq = 0;

export const ChannelFlowGraph = ({ flow }: { flow: CFlow }) => {
  const [svg, setSvg] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const mermaid = (await import("mermaid")).default;
      mermaid.initialize({
        startOnLoad: false,
        securityLevel: "strict",
        theme: "neutral",
        flowchart: { padding: 8 },
      });
      const { svg } = await mermaid.render(
        `channel-flow-${++renderSeq}`,
        toMermaid(flow)
      );
      if (!cancelled) setSvg(svg);
    })().catch(() => {
      if (!cancelled) setSvg("");
    });
    return () => {
      cancelled = true;
    };
  }, [flow]);

  if (!svg) return null;

  return (
    <div
      className="overflow-x-auto rounded-md border bg-background p-3 [&_svg]:max-w-none"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
};

export default ChannelFlowGraph;
